import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import {
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
} from "../api/apiServices";

const ITEMS_PER_PAGE = 20;

export default function LocationPage() {
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [form, setForm] = useState({
    country: "",
    state: "",
    city: "",
    isActive: true,
  });

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const res = await getLocations();
      setLocations(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const filtered = locations.filter((l) => {
    const q = search.toLowerCase();
    return (
      l.city.toLowerCase().includes(q) ||
      l.state.toLowerCase().includes(q) ||
      l.country.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const submitForm = async () => {
    if (editing) {
      await updateLocation(editing._id, form);
    } else {
      await createLocation(form);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ country: "", state: "", city: "", isActive: true });
    fetchLocations();
  };

  const handleDelete = async () => {
    await deleteLocation(confirmDelete._id);
    setConfirmDelete(null);
    fetchLocations();
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Hero */}
      <div className="h-[260px] bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center text-white">
        <h1 className="text-4xl font-extrabold">Locations</h1>
      </div>

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 mt-12 flex justify-between items-center">
        <input
          placeholder="Search city, state or country..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full md:w-1/2 px-4 py-2 border rounded-lg"
        />

        <button
          onClick={() => setShowForm(true)}
          className="ml-4 flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg"
        >
          <FaPlus /> Add Location
        </button>
      </div>

      {/* Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-6xl mx-auto px-4 mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {loading ? (
          <p>Loading locations...</p>
        ) : (
          paginated.map((loc) => (
            <div
              key={loc._id}
              className="bg-white rounded-xl shadow p-5"
            >
              <div className="flex justify-between mb-2">
                <div>
                  <p className="font-bold text-lg">{loc.city}</p>
                  <p className="text-sm text-gray-500">
                    {loc.state}, {loc.country}
                  </p>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    loc.isActive
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {loc.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => {
                    setEditing(loc);
                    setForm(loc);
                    setShowForm(true);
                  }}
                  className="text-indigo-600"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => setConfirmDelete(loc)}
                  className="text-red-600"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        )}
      </motion.div>

      {/* Pagination */}
      <div className="flex justify-center gap-4 mt-10">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-full max-w-md">
            <h3 className="font-bold mb-4">
              {editing ? "Edit Location" : "Add Location"}
            </h3>

            {["country", "state", "city"].map((field) => (
              <input
                key={field}
                placeholder={field}
                value={form[field]}
                onChange={(e) =>
                  setForm({ ...form, [field]: e.target.value })
                }
                className="w-full mb-3 px-4 py-2 border rounded"
              />
            ))}

            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.checked })
                }
              />
              Active
            </label>

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowForm(false)}>Cancel</button>
              <button
                onClick={submitForm}
                className="bg-indigo-600 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-full max-w-sm">
            <p className="mb-4">
              Delete location <b>{confirmDelete.city}</b>?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-4 py-2 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
